/*
 * @Author: Nora Galvin
 * @Date: 2020-08-24 12:33:07
 * @Last Modified by: Nora
 * @Last Modified time: 2020-09-04 17:19:27
 */

import type { TypeAnimation } from './animations/type';
import type { TypeInteraction, TypeOption, TypeInteractionExport, TypeAnimationExport } from './type';
import { animations } from './configs/animations';
import { titleize, atomIDToBkID } from '@/app/utils/helpers';
//default interaction json
import defaultJSON from '@/app/editor/sidebar/interactions/templates/contentbk-template.html';
import singleInteractionTemplate from '@/app/editor/sidebar/interactions/templates/single-interaction.html';
import Animation from './animations';
import DropdownControl from '@/app/editor/sidebar/interactions/controls/dropdown';
import SidebarPopUp from '@/app/editor/sidebar/components/popup/';
import { app } from '@/store';
import { base } from '@/app/editor/sidebar/constant';

/**
 * Interaction Class
 */
export default class Interaction {
  protected readonly _animations: Array<TypeAnimation> = animations;
  _$atom: JQuery;
  _$interactionPanel: JQuery;
  _$gfLeftSideBar: JQuery;
  _$interactionTab: JQuery;
  _$gfFrame: JQuery;
  _options: any;

  // For singleton
  _selectedAnimation: Animation;
  _interaction: TypeInteraction;

  /**
   * Constructor
   * @param interaction ..
   * @param $atom ..
   * @param animation ..
   * @param options ..
   */
  constructor(interaction: TypeInteraction, $atom: JQuery, animation?: TypeAnimation, options?: any) {
    this._$atom = $atom;
    this._$gfLeftSideBar = jQuery('#gfLeftSideBar');
    this._$interactionTab = jQuery('.simple-tabs');
    this._$interactionPanel = this._$interactionTab.find('.interactions-panel');
    if (options) {
      this._options = options;
    }
    this._$gfFrame = jQuery('#gfFrame');
    this._interaction = interaction;

    this.renderItem(animation);
  }

  /**
   * Convert animation array to option array
   * @returns array options
   */
  animations2Options(): Array<TypeOption> {
    const options: Array<TypeOption> = [{ key: 'none', value: 'None' }];

    animations.forEach((e) => {
      const added = options.find((el) => el.type == 'group' && el.key == e.type);
      if (added) {
        if (!added.options) {
          added.options = [];
        }
        added.options.push({ key: e.key, value: e.name, default: e.default });
      } else {
        options.push({
          key: e.type,
          value: titleize(e.type),
          type: 'group',
          options: [
            {
              key: e.key,
              value: e.name,
              default: e.default,
            },
          ],
        });
      }
    });

    return options;
  }

  /**
   * Add item when click
   * @param animation ..
   * @returns void
   */
  renderItem(animation?: TypeAnimation): void {
    // Validate exist interaction
    const interactionKey = `${this._interaction.key}_${this._interaction.type}_${this._$atom.attr('id')}`;
    const existInteraction = this._$interactionPanel
      .find('.interactions-list')
      .find(`.interaction-item[data-interaction=${interactionKey}]`);

    if (existInteraction && existInteraction.length) {
      // alert("Interaction is added");
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const _this = this;
    this._$interactionPanel.find('.interactions-empty').hide();
    const options = this.animations2Options();
    const interactionItem = singleInteractionTemplate(this._interaction, this._$atom, options);
    const $interactionItem = $(interactionItem);

    ($interactionItem.find('.gf-dropdown-list').addClass('optiscroll') as any).optiscroll();

    $interactionItem
      .find('.interaction-head')
      .off('click')
      .on('click', function (event) {
        // Expand all other
        const otherItems = _this._$interactionPanel
          .find('.interactions-list')
          .find('.interaction-item')
          .not($interactionItem);
        otherItems.removeClass('active');
        otherItems.find('.interaction-content').slideUp(200);
        if (!($(event.target).is('span.delete') || $(event.target).parents('span.delete').is('span.delete'))) {
          $interactionItem.toggleClass('active');
          $interactionItem.find('.interaction-content').slideToggle(200);
        }
      });

    const opts = {
      /**
       * onChange
       * @param data ..
       */
      onChange: async (data: any) => {
        if (data && data.key) {
          const anim = _this._animations.find((e) => e.key == data.key);
          $interactionItem.find('.interaction-content .interaction-section').not('.interaction-section:first').remove();
          $interactionItem.find('.interaction-content .button-group').remove();
          if (anim) {
            // Add callback onChange to update bkcontent
            const animOpts = {
              /**
               * onChange
               * @param data ..
               */
              onChange: (data: any) => {
                this.updateContentBK(data);
              },
            };
            if (!this._selectedAnimation) {
              this._selectedAnimation = new Animation(anim, $interactionItem, this._$atom, animOpts);
            } else {
              this._selectedAnimation.setAnimation(anim);
            }
            await this._selectedAnimation.runAnimate();
          }
        }
      },
      /**
       * onOpened
       */
      onOpened: () => {
        _this._$interactionPanel.find('.interactions-overlay').fadeIn();
      },
      /**
       * onClosed
       */
      onClosed: () => {
        _this._$interactionPanel.find('.interactions-overlay').fadeOut();
      },
    };

    const dropdown = new DropdownControl($interactionItem.find('select'), opts);

    if (animation) {
      dropdown.setValue(animation.key);
      if (!this._selectedAnimation) {
        const animOpts = {
          /**
           * onChange
           * @param data ..
           */
          onChange: (data: any) => {
            this.updateContentBK(data);
          },
        };

        this._selectedAnimation = new Animation(animation, $interactionItem, this._$atom, animOpts);
      } else {
        this._selectedAnimation.setAnimation(animation);
      }
    }

    // Delete DOM and contentBK
    $interactionItem
      .find('.delete')
      .off('click')
      .on('click', function () {
        //
        new SidebarPopUp({
          title: 'Deleting This Integration?',
          description: 'This integration will be removed from your integrations list.',
          primaryButton: {
            text: 'Delete',
            class: 'gf_btn-danger', // check or add class in style.scss on component
          },
          secondaryButton: {
            text: 'Cancel',
          },
          /**
           * On click delete
           */
          onClickedPrimary() {
            // Remove data in contentBk
            const atomID = _this._$atom.attr('id') || '';
            const contentBkID = atomIDToBkID(atomID);

            let interactionJSON: TypeInteractionExport | undefined = undefined;
            const interaction = (app as any).$store.getters['editor/GET_ANIMATION_INTERACTION_SETTING_BY_KEY'](
              base._interactions + '_' + contentBkID,
            );
            if (interaction) {
              interactionJSON = JSON.parse(interaction);
            } else {
              interactionJSON = defaultJSON({ animation: [] });
            }
            if (interactionJSON && interactionJSON.animations) {
              const animations = interactionJSON.animations;
              for (let i = 0; i < animations.length; i++) {
                if (
                  animations[i].interaction.key == _this._interaction.key &&
                  animations[i].interaction.type == _this._interaction.type
                ) {
                  interactionJSON.animations.splice(i, 1);
                  break;
                }
              }
              (app as any).$store.commit('editor/SET_ANIMATION_DATA_SETTINGS', {
                id: base._interactions + '_' + contentBkID,
                type: 'interaction',
                value: JSON.stringify(interactionJSON),
              });
            }

            // Remove DOM
            $interactionItem.remove();

            // Emit event
            if (_this._options && _this._options.onInteractionDeleted) {
              _this._options.onInteractionDeleted(_this._interaction);
            }
          },
        });
      });

    // Always expand integration
    $interactionItem.addClass('active');
    this._$interactionPanel.find('.interactions-list').append($interactionItem);
    return;
  }

  /**
   * Update animation settings in content bk
   * @param json ..
   * @returns void
   */
  updateContentBK(json: TypeAnimationExport): void {
    const atomID = this._$atom.attr('id') || '';
    const contentBkID = atomIDToBkID(atomID);

    let interactionJSON: TypeInteractionExport | undefined = undefined;
    const interaction = (app as any).$store.getters['editor/GET_ANIMATION_INTERACTION_SETTING_BY_KEY'](
      base._interactions + '_' + contentBkID,
    );
    if (interaction) {
      interactionJSON = JSON.parse(interaction);
    } else {
      interactionJSON = defaultJSON({ animation: json });
    }

    if (interactionJSON && interactionJSON.animations) {
      const animations = interactionJSON.animations;
      let found = false;
      for (let i = 0; i < animations.length; i++) {
        if (
          animations[i].interaction.key == json.interaction.key &&
          animations[i].interaction.type == json.interaction.type
        ) {
          animations[i] = json;
          found = true;
        }
      }
      if (!found) {
        interactionJSON.animations.push(json);
      }

      (app as any).$store.commit('editor/SET_ANIMATION_DATA_SETTINGS', {
        id: base._interactions + '_' + contentBkID,
        type: 'interaction',
        value: JSON.stringify(interactionJSON),
      });
    }
    return;
  }
}
